import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  fbaGrowthIntelligenceApi,
  type DemandForecast,
  type ExperimentLink,
  type InventoryRecommendation,
  type InventoryHealthScore,
  type InventoryTimelineEvent,
  type OpportunityInsight,
  type PlanVersion,
  type ReplenishmentPlan,
  type ScenarioPlan,
  type ShipmentPlan,
  type ShipmentSimulationResult,
  type VelocityInsight,
} from '../../api/fba-growth-intelligence';

type Theme = 'light' | 'dark';
type Tab = 'health' | 'forecast' | 'scenarios' | 'recommendations' | 'shipments' | 'opportunities' | 'versions' | 'experiments' | 'timeline';

const DEFAULT_ASIN = 'B0CX123456';
const priorityCls = (p: string) =>
  p === 'critical' || p === 'high' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
    : p === 'medium' ? 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200'
    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';

export function FBAGrowthIntelligencePage() {
  const { asin: routeAsin } = useParams<{ asin: string }>();
  const asin = routeAsin ?? DEFAULT_ASIN;
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('health');
  const [health, setHealth] = useState<InventoryHealthScore | null>(null);
  const [forecast, setForecast] = useState<DemandForecast | null>(null);
  const [velocity, setVelocity] = useState<VelocityInsight[]>([]);
  const [replenishment, setReplenishment] = useState<ReplenishmentPlan | null>(null);
  const [scenarios, setScenarios] = useState<ScenarioPlan[]>([]);
  const [recommendations, setRecommendations] = useState<InventoryRecommendation[]>([]);
  const [shipments, setShipments] = useState<ShipmentPlan[]>([]);
  const [opportunities, setOpportunities] = useState<OpportunityInsight[]>([]);
  const [versions, setVersions] = useState<PlanVersion[]>([]);
  const [experiments, setExperiments] = useState<ExperimentLink[]>([]);
  const [timeline, setTimeline] = useState<InventoryTimelineEvent[]>([]);
  const [simulation, setSimulation] = useState<ShipmentSimulationResult | null>(null);
  const [simulating, setSimulating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = dark ? 'border-emerald-900/40 bg-zinc-900' : 'border-emerald-200 bg-white';
  const muted = dark ? 'text-zinc-400' : 'text-slate-500';
  const shell = dark ? 'bg-zinc-950 text-zinc-100' : 'bg-emerald-50/20 text-slate-900';
  const accent = dark ? 'text-emerald-300' : 'text-emerald-700';
  const tabActive = 'bg-emerald-600 text-white';

  const refresh = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const [h, fc, vel, rep, scn, recs, ships, opps, vers, exps, tl] = await Promise.all([
        fbaGrowthIntelligenceApi.health(asin), fbaGrowthIntelligenceApi.forecast(asin),
        fbaGrowthIntelligenceApi.velocity(asin), fbaGrowthIntelligenceApi.replenishment(asin),
        fbaGrowthIntelligenceApi.scenarios(asin), fbaGrowthIntelligenceApi.recommendations(asin),
        fbaGrowthIntelligenceApi.shipments(asin), fbaGrowthIntelligenceApi.opportunities(asin),
        fbaGrowthIntelligenceApi.versions(asin), fbaGrowthIntelligenceApi.experiments(asin),
        fbaGrowthIntelligenceApi.history(asin),
      ]);
      setHealth(h); setForecast(fc); setVelocity(vel); setReplenishment(rep); setScenarios(scn);
      setRecommendations(recs); setShipments(ships); setOpportunities(opps); setVersions(vers);
      setExperiments(exps); setTimeline(tl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load FBA intelligence');
    } finally { setLoading(false); }
  }, [asin]);

  useEffect(() => { refresh(); }, [refresh]);

  const runSimulation = async (planId: string) => {
    setSimulating(planId); setSimulation(null);
    try { setSimulation(await fbaGrowthIntelligenceApi.simulate(asin, planId)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Simulation failed'); }
    finally { setSimulating(null); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'health', label: 'Health' }, { id: 'forecast', label: 'Forecast' }, { id: 'scenarios', label: 'Scenarios' },
    { id: 'recommendations', label: 'Optimization' }, { id: 'shipments', label: 'Shipments' },
    { id: 'opportunities', label: 'Opportunities' }, { id: 'versions', label: 'Versions' },
    { id: 'experiments', label: 'Experiments' }, { id: 'timeline', label: 'Timeline' },
  ];

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>DEV-000108 · FBA Growth Intelligence · Observe → Learn → Improve</p>
            <h1 className="text-xl font-semibold">{health?.product_name ?? 'Product'} — {asin}</h1>
            {health && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>Health <strong className={accent}>{health.overall_score}/100</strong></span>
                <span>{health.dna_origin ? 'DNA-backed' : 'Pending DNA'}</span>
                {forecast && <span>Forecast {forecast.expected_units} units/mo</span>}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to={`/products/${asin}/workspace`} className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>ASIN 360</Link>
            <Link to="/platform/fba-growth-intelligence-dev108" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Sprint Center</Link>
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>{dark ? 'Light' : 'Dark'}</button>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'health' && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? <div className="h-20 animate-pulse rounded-xl bg-slate-200" /> : health?.components.map((c) => (
            <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold">{c.name}</p>
                {c.priority && <span className={`rounded px-2 py-0.5 text-xs ${priorityCls(c.priority)}`}>{c.priority}</span>}
              </div>
              <p className={`mt-1 text-2xl font-bold ${accent}`}>{c.current_value}<span className={`text-sm font-normal ${muted}`}> / {c.target}</span></p>
            </div>
          ))}
        </div>
      )}

      {tab === 'forecast' && forecast && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className="text-2xl font-bold">{forecast.expected_units} <span className={`text-sm font-normal ${muted}`}>units/mo</span></p>
          <p className={`text-sm ${muted}`}>{forecast.expected_revenue} revenue · {forecast.expected_profit} profit · {(forecast.confidence * 100).toFixed(0)}% confidence</p>
          <p className={`mt-2 text-sm ${accent}`}>Range: {forecast.prediction_range}</p>
          {forecast.forecast_drivers && <ul className={`mt-2 list-disc pl-5 text-xs ${muted}`}>{forecast.forecast_drivers.map((d) => <li key={d}>{d}</li>)}</ul>}
          {velocity.length > 0 && (
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              {velocity.map((v) => (
                <div key={v.period} className={`rounded-lg border p-2 text-sm ${card}`}>
                  <p className="font-medium capitalize">{v.period}</p>
                  <p>{v.units} units · {v.trend}</p>
                </div>
              ))}
            </div>
          )}
          {replenishment && (
            <div className={`mt-4 rounded-lg border p-3 ${card}`}>
              <p className="font-semibold">Replenishment: {replenishment.recommended_qty} units by {replenishment.recommended_date}</p>
              <p className={`text-xs ${muted}`}>{replenishment.reason}</p>
            </div>
          )}
        </div>
      )}

      {tab === 'scenarios' && (
        <div className="grid gap-3 sm:grid-cols-3">
          {scenarios.map((s) => (
            <div key={s.id} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{s.name}</p>
              <p className={`text-lg font-bold ${accent}`}>{s.units} units</p>
              <p className={`text-xs ${muted}`}>{s.expected_profit} · Stockout {s.stockout_probability} · Overstock {s.overstock_probability}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-3">
          {recommendations.map((r) => (
            <div key={r.id} className={`rounded-xl border p-4 ${card}`}>
              <span className={`rounded px-2 py-0.5 text-xs ${priorityCls(r.priority)}`}>{r.priority}</span>
              <h3 className="mt-1 font-semibold">{r.title}</h3>
              <p className={`text-sm ${muted}`}>{r.business_objective}</p>
              {r.dna_evidence && <span className={`text-xs ${accent}`}>✓ DNA evidence</span>}
              <p className={`mt-1 text-xs ${muted}`}>{r.expected_profit} · {(r.confidence * 100).toFixed(0)}% confidence</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'shipments' && (
        <div className="space-y-3">
          {shipments.map((s) => (
            <div key={s.id} className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border p-4 ${card}`}>
              <div>
                <p className="font-medium">{s.id} — {s.quantity} units → {s.destination_fc}</p>
                <p className={`text-xs ${muted}`}>{s.expected_arrival} · {s.estimated_cost} · {s.status}</p>
              </div>
              <button type="button" disabled={simulating === s.id} onClick={() => runSimulation(s.id)}
                className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs text-white hover:bg-emerald-500 disabled:opacity-50">
                {simulating === s.id ? 'Simulating…' : 'Simulate Shipment'}
              </button>
            </div>
          ))}
          {simulation && (
            <div className={`rounded-xl border border-emerald-300 p-4 ${card}`}>
              <h3 className="font-semibold">Shipment Simulation</h3>
              <p className={`text-sm ${muted}`}>Arrival {simulation.arrival_date} · Stockout {simulation.stockout_probability} · {simulation.inventory_days} days cover</p>
              <p className={`text-sm ${accent}`}>{simulation.expected_profit} · {(simulation.confidence * 100).toFixed(0)}% confidence</p>
            </div>
          )}
        </div>
      )}

      {tab === 'opportunities' && opportunities.map((o) => (
        <div key={o.id} className={`rounded-xl border p-3 ${card}`}>
          <p className="font-medium">{o.title}</p>
          <p className={`text-xs ${muted}`}>{o.type} · {o.expected_profit} · <span className={priorityCls(o.priority)}>{o.priority}</span></p>
        </div>
      ))}

      {tab === 'versions' && versions.map((v) => (
        <div key={v.id} className={`flex justify-between rounded-xl border p-3 ${card}`}>
          <div><p className="font-medium">v{v.version} {v.type} — {v.status}</p><p className={`text-xs ${muted}`}>{v.summary}</p></div>
          <span>{v.approved ? 'Approved' : 'Draft'}</span>
        </div>
      ))}

      {tab === 'experiments' && experiments.map((e) => (
        <div key={e.id} className={`rounded-xl border p-3 ${card}`}>
          <p className="font-medium">{e.name}</p>
          <p className={`text-xs ${muted}`}>{e.status} · {e.baseline_kpi}</p>
        </div>
      ))}

      {tab === 'timeline' && timeline.map((e) => (
        <div key={e.id} className={`flex gap-3 border-b py-2 ${card}`}>
          <time className={`shrink-0 text-xs ${muted}`}>{new Date(e.timestamp).toLocaleString()}</time>
          <span className="text-sm">{e.title}</span>
        </div>
      ))}
    </div>
  );
}
